import Button from "../../../../../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../../../../../components/button/types";
import { useTranslation } from "react-i18next";
import {
  fetchCvCheckCoaches,
  uploadCVAndCreateCredit,
} from "../../../../../../../../../services/selfAssessment.service";
import { ReactComponent as CloseIcon } from "../../../../../../../../../assets/close.svg";
import { AuthContext } from "../../../../../../../../../contexts/AuthContext";
import { useMutation, useQuery } from "react-query";
import { useEffect, useMemo, useState } from "react";
import { Coach } from "../../../../LinkedInPackage";
import { fetchCoaches } from "../../../../../../../../../services/linkedin-package.service";
import { CircularImage } from "../../../../../../../../../components/circularImage";
import { CVCheck } from "../../..";

export interface CvCheckCoach {
  id: number;
  name: string;
  profilePicture?: string;
  platformContentId: string;
}

function FileDownloader({ file }: { file: any }) {
  return (
    <a
      href={file ? URL.createObjectURL(file) : ""}
      download={file ? file.name : ""}
      target="_blank"
      rel="noopener noreferrer"
    >
      <button className="text-[#757575] ">
        {file ? (
          <>
            <span className="break-all">{file.name}</span>{" "}
            <span className="text-Grey-dark ml-3">
              {(file.size / 1024 / 1024).toFixed(2)} mb
            </span>
          </>
        ) : (
          " .pdf file"
        )}
      </button>
    </a>
  );
}

export function TemplatePreview({
  onNext,
  onBack,
  selectedTemplate,
  cvToUpload,
  templates,
  checkType,
  deleteTemplate,
  deleteFile,
  selectedCoach,
  isLoadingCoaches,
  canChangeCoach,
  onTriggerCoachSelection,
}: {
  onNext: () => void;
  onBack: () => void;
  selectedTemplate: number;
  cvToUpload: any;
  templates: any;
  checkType: string;
  deleteTemplate: () => void;
  deleteFile: () => void;
  selectedCoach: CvCheckCoach | null;
  isLoadingCoaches: boolean;
  canChangeCoach: boolean | undefined;
  onTriggerCoachSelection: () => void;
}) {
  const { t } = useTranslation("myArea");

  const [buttonDisable, setButtonDisabled] = useState(false);

  const handleNextClick = async () => {
    try {
      if (buttonDisable) return;

      if (checkType === "Plus" && !selectedCoach) {
        console.error("No coach selected for CV Check 'Plus' submission.");
        return;
      }

      setButtonDisabled(true);
      const selectedTemplateName = templates[selectedTemplate].name;
      const cvCheckType = checkType;

      const professionalIdToSend =
        checkType === "Plus" && selectedCoach
          ? selectedCoach.platformContentId
          : undefined;

      await uploadCVAndCreateCredit(
        cvToUpload[0],
        selectedTemplateName,
        cvCheckType,
        professionalIdToSend
      );
      onNext();
    } catch (error) {
      setButtonDisabled(false);
      console.error("Error submitting CV check:", error);
    }
  };

  const ensureProtocol = (url?: string): string | undefined => {
    if (!url) return undefined;
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    if (url.startsWith("//")) {
      return `https:${url}`;
    }

    return undefined;
  };

  return (
    <div className="flex flex-col w-full ">
      <div className="flex flex-col w-full ">
        <div className="flex flex-col py-4">
          <div className="flex flex-col w-full p-4 bg-gray-300 ">
            <h2 className="font-bold mb-4">
              {t(`assessments.cv-check.template`)}
            </h2>
            <div className="flex w-full justify-between">
              <div className="w-1/4 flex items-center">
                <img
                  width={300}
                  className="cursor-pointer"
                  src={templates[selectedTemplate].templateUrl}
                  alt={`Selected Template`}
                />
                <div
                  className="ml-5 font-bold"
                  style={{ whiteSpace: "nowrap" }}
                >
                  {templates[selectedTemplate].name}
                </div>
              </div>
              <CloseIcon
                onClick={deleteTemplate}
                className="w-6 cursor-pointer"
              />
            </div>
          </div>
          <div className="w-full justify-center items-center p-4 ">
            <h2 className="font-bold mb-2">
              {t(`assessments.cv-check.your-cv`)}
            </h2>
            <div className="flex w-full justify-between items-center space-x-2">
              <FileDownloader file={cvToUpload[0]} />
              <CloseIcon onClick={deleteFile} className="w-6 cursor-pointer " />
            </div>
          </div>

          {checkType === "Plus" && (
            <div className="w-full px-4  ">
              {!isLoadingCoaches && selectedCoach && (
                <div>
                  <div>
                    <h2 className="font-bold mb-2">
                      {t("assessments.cv-check.coachLabel")}{" "}
                    </h2>
                    {isLoadingCoaches ? (
                      <span className="text-gray-500">
                        {t("assessments.cv-check.loadingCoach")}
                      </span>
                    ) : selectedCoach ? (
                      <span className="block text-[#757575] mb-2">
                        {selectedCoach.name}
                      </span>
                    ) : (
                      <span className="text-red-600">
                        {t("assessments.cv-check.noCoachAssigned")}
                      </span>
                    )}
                  </div>

                  <div>
                    {canChangeCoach && (
                      <button
                        onClick={onTriggerCoachSelection}
                        className="items-center underline text-[#757575] font-semibold hover:text-gray-700 focus:outline-none"
                      >
                        {t("assessments.cv-check.changeCoachButton")}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div
        className="w-full py-3 px-4 lg:py-0 lg:px-0 
  fixed bottom-0 left-0 bg-white 
  lg:relative lg:bottom-auto lg:left-auto lg:bg-transparent"
      >
        <div className="flex items-center justify-between">
          <Button
            className="w-full mr-2"
            variant={ButtonVariant.SECONDARY}
            size={ButtonSize.SMALL}
            onClick={onBack}
          >
            {t(`assessments.cv-check.back-button`)}
          </Button>
          <Button
            className="w-full ml-2"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SMALL}
            onClick={handleNextClick}
            disabled={buttonDisable}
          >
            {t(`assessments.cv-check.submit-button`)}
          </Button>
        </div>
      </div>
    </div>
  );
}
